<template>
  <div class="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
    <div class="glass-strong rounded-xl p-6 w-full max-w-lg mx-4">
      <h3 class="text-xl font-bold text-white mb-4">Reserve Equipment</h3>
      <form @submit.prevent="submit">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="md:col-span-2">
            <label class="block text-sm text-slate-300 mb-1">Organization</label>
            <div class="flex gap-2">
              <input
                v-model="orgQuery"
                placeholder="Search organizations"
                class="flex-1 px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <button type="button" class="px-3 py-2 rounded-lg btn-primary" @click="createOrg">
                New
              </button>
            </div>
            <select
              v-model.number="organizationId"
              class="mt-2 w-full px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
            >
              <option :value="undefined">Select...</option>
              <option v-for="o in filteredOrgs" :key="o.id" :value="o.id">
                {{ o.name }} ({{ o.type }})
              </option>
            </select>
          </div>
          <div class="md:col-span-2">
            <label class="block text-sm text-slate-300 mb-1">Contact</label>
            <div class="flex gap-2">
              <input
                v-model="contactQuery"
                placeholder="Search consignees"
                class="flex-1 px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <button type="button" class="px-3 py-2 rounded-lg btn-primary" @click="createContact">
                New
              </button>
            </div>
            <select
              v-model.number="contactId"
              class="mt-2 w-full px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
            >
              <option :value="undefined">Select...</option>
              <option v-for="c in filteredContacts" :key="c.id" :value="c.id">
                {{ c.name }} - {{ c.email }}
              </option>
            </select>
            <button
              type="button"
              class="mt-2 text-xs text-primary-300 hover:text-primary-200 underline"
              @click="goToConsignees"
            >
              Manage Consignees
            </button>
          </div>

          <div class="md:col-span-2 rounded-lg border border-slate-700/60 bg-slate-900/40 p-3 space-y-3">
            <p class="text-xs uppercase tracking-[0.15em] text-slate-400">Or Enter New Consignee</p>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                v-model="newConsigneeName"
                placeholder="Consignee name"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <input
                v-model="newConsigneeTitle"
                placeholder="Title"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <input
                v-model="newConsigneeEmail"
                placeholder="Email"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <input
                v-model="newConsigneePhone"
                placeholder="Phone"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <input
                v-model="newConsigneeOrganization"
                placeholder="Organization (optional)"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <input
                v-model="newConsigneeAddress1"
                placeholder="Address 1"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <input
                v-model="newConsigneeAddress2"
                placeholder="Address 2"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <input
                v-model="newConsigneeCity"
                placeholder="City"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <div class="grid grid-cols-2 gap-3">
                <input
                  v-model="newConsigneeState"
                  placeholder="State"
                  class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white uppercase"
                  maxlength="2"
                />
                <input
                  v-model="newConsigneeZip"
                  placeholder="ZIP"
                  class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
                />
              </div>
            </div>
            <label class="inline-flex items-center gap-2 text-sm text-slate-300">
              <input v-model="newConsigneeShippingSameAsConsignee" type="checkbox" />
              Shipping address same as Consignee address
            </label>

            <div
              v-if="!newConsigneeShippingSameAsConsignee"
              class="grid grid-cols-1 md:grid-cols-2 gap-3"
            >
              <input
                v-model="newConsigneeShippingAddress1"
                placeholder="Shipping Address 1"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <input
                v-model="newConsigneeShippingAddress2"
                placeholder="Shipping Address 2"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <input
                v-model="newConsigneeShippingCity"
                placeholder="Shipping City"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
              />
              <div class="grid grid-cols-2 gap-3">
                <input
                  v-model="newConsigneeShippingState"
                  placeholder="Shipping State"
                  class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white uppercase"
                  maxlength="2"
                />
                <input
                  v-model="newConsigneeShippingZip"
                  placeholder="Shipping ZIP"
                  class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
                />
              </div>
            </div>
          </div>
          <div>
            <label class="block text-sm text-slate-300 mb-1">Start</label>
            <input
              v-model="startAt"
              type="datetime-local"
              required
              class="w-full px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[--ring]"
            />
          </div>
          <div>
            <label class="block text-sm text-slate-300 mb-1">End</label>
            <input
              v-model="endAt"
              type="datetime-local"
              required
              class="w-full px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[--ring]"
            />
          </div>
          <div>
            <label class="block text-sm text-slate-300 mb-1">Purpose</label>
            <select
              v-model="purpose"
              required
              class="w-full px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
            >
              <option value="poc">Proof of Concept</option>
              <option value="test">Test</option>
              <option value="tech_eval">Tech Eval</option>
            </select>
          </div>
          <div>
            <label class="block text-sm text-slate-300 mb-1">Notes</label>
            <input
              v-model="notes"
              class="w-full px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
            />
          </div>
        </div>
        <p v-if="error" class="text-red-400 text-sm mt-2">{{ error }}</p>
        <div class="flex space-x-3 mt-6">
          <button
            type="button"
            @click="$emit('close')"
            class="flex-1 px-4 py-2 bg-slate-800/70 border border-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[--ring]"
          >
            Cancel
          </button>
          <button type="submit" class="flex-1 px-4 py-2 rounded-lg btn-primary">Reserve</button>
        </div>
      </form>
    </div>

    <div v-if="showSaveConsigneePrompt" class="fixed inset-0 bg-black/70 flex items-center justify-center z-[60]">
      <div class="glass-strong rounded-xl p-5 w-full max-w-md mx-4">
        <h4 class="text-lg font-semibold text-white">Save as New Consignee?</h4>
        <p class="text-sm text-slate-300 mt-2">
          This reservation includes a new consignee. Save it to the Consignee directory before reserving?
        </p>
        <div class="mt-4 flex justify-end gap-3">
          <button
            type="button"
            class="px-4 py-2 rounded border border-slate-600 text-slate-300 hover:text-white hover:border-slate-400"
            @click="showSaveConsigneePrompt = false"
          >
            Cancel
          </button>
          <button type="button" class="px-4 py-2 rounded btn-primary" :disabled="submitting" @click="saveConsigneeAndReserve">
            {{ submitting ? 'Saving...' : 'Save and Reserve' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showDuplicateConsigneePrompt" class="fixed inset-0 bg-black/70 flex items-center justify-center z-[70]">
      <div class="glass-strong rounded-xl p-5 w-full max-w-lg mx-4">
        <h4 class="text-lg font-semibold text-white">Possible Duplicate Consignee</h4>
        <p class="text-sm text-slate-300 mt-2">
          We found existing consignees with the same email or phone. Use an existing entry or create a new one anyway.
        </p>
        <div class="mt-3 max-h-52 overflow-auto space-y-2">
          <button
            v-for="match in duplicateConsigneeMatches"
            :key="match.id"
            type="button"
            class="w-full text-left px-3 py-2 rounded border border-slate-700 bg-slate-900/50 hover:border-primary-500/60"
            @click="useExistingDuplicate(match.id, match.organization_id)"
          >
            <div class="text-sm text-white">{{ match.name || 'Unnamed consignee' }}</div>
            <div class="text-xs text-slate-400">
              {{ match.email || 'No email' }} • {{ match.phone || 'No phone' }}
            </div>
          </button>
        </div>
        <div class="mt-4 flex justify-end gap-3">
          <button
            type="button"
            class="px-4 py-2 rounded border border-slate-600 text-slate-300 hover:text-white hover:border-slate-400"
            @click="cancelDuplicatePrompt"
          >
            Cancel
          </button>
          <button
            type="button"
            class="px-4 py-2 rounded btn-primary"
            :disabled="submitting"
            @click="createNewDuplicateAnyway"
          >
            {{ submitting ? 'Saving...' : 'Create New Anyway' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { createReservation } from '@/lib/appApi'
import { supabase } from '@/lib/supabaseClient'
import { useRouter } from 'vue-router'

const props = defineProps<{ equipmentId: number }>()
const emit = defineEmits(['close', 'saved'])

const startAt = ref('')
const endAt = ref('')
const purpose = ref<'poc' | 'test' | 'tech_eval'>('poc')
const notes = ref('')
const error = ref('')
const submitting = ref(false)
const showSaveConsigneePrompt = ref(false)
const showDuplicateConsigneePrompt = ref(false)
const duplicateConsigneeMatches = ref<Array<{ id: number; name?: string; email?: string; phone?: string; organization_id?: number | null }>>([])
const organizationId = ref<number | undefined>(undefined)
const contactId = ref<number | undefined>(undefined)
const orgs = ref<any[]>([])
const contacts = ref<any[]>([])
const orgQuery = ref('')
const contactQuery = ref('')
const router = useRouter()

const newConsigneeOrganization = ref('')
const newConsigneeName = ref('')
const newConsigneeTitle = ref('')
const newConsigneeEmail = ref('')
const newConsigneePhone = ref('')
const newConsigneeAddress1 = ref('')
const newConsigneeAddress2 = ref('')
const newConsigneeCity = ref('')
const newConsigneeState = ref('')
const newConsigneeZip = ref('')
const newConsigneeShippingSameAsConsignee = ref(true)
const newConsigneeShippingAddress1 = ref('')
const newConsigneeShippingAddress2 = ref('')
const newConsigneeShippingCity = ref('')
const newConsigneeShippingState = ref('')
const newConsigneeShippingZip = ref('')

const hasManualConsignee = computed(() =>
  Boolean(
    newConsigneeName.value.trim() ||
      newConsigneeEmail.value.trim() ||
      newConsigneePhone.value.trim() ||
      newConsigneeAddress1.value.trim(),
  ),
)
const filteredOrgs = computed(() =>
  orgs.value.filter((o) => (o.name || '').toLowerCase().includes(orgQuery.value.toLowerCase())),
)
const filteredContacts = computed(() =>
  contacts.value.filter((c) =>
    (c.name || '').toLowerCase().includes(contactQuery.value.toLowerCase()),
  ),
)

async function performReservation(contactIdOverride?: number, organizationIdOverride?: number) {
  const startIso = new Date(startAt.value).toISOString()
  const endIso = new Date(endAt.value).toISOString()
  const { data: conflicts } = await supabase
    .schema('app' as any).from('reservations')
    .select('id')
    .eq('equipment_id', props.equipmentId)
    .or(`and(start_at,lt.${endIso}),and(end_at,gt.${startIso})`)
    .limit(1)
  if (conflicts && conflicts.length) {
    throw new Error('Time conflict with existing reservation')
  }
  await createReservation({
    equipmentId: props.equipmentId,
    purpose: purpose.value,
    startAt: startIso,
    endAt: endIso,
    notes: notes.value,
    organizationId: organizationIdOverride ?? organizationId.value,
    contactId: contactIdOverride ?? contactId.value,
  })
}

async function submit() {
  try {
    error.value = ''
    if (!contactId.value && hasManualConsignee.value) {
      showSaveConsigneePrompt.value = true
      return
    }
    if (!contactId.value && !hasManualConsignee.value) {
      error.value = 'Select a consignee or enter new consignee details.'
      return
    }
    await performReservation()
    emit('saved')
    emit('close')
  } catch (e: any) {
    error.value = e.message || 'Failed to reserve'
  }
}

function normalizePhone(value: string) {
  return value.replace(/\D/g, '')
}

function getDuplicateConsigneeMatches() {
  const email = newConsigneeEmail.value.trim().toLowerCase()
  const phone = normalizePhone(newConsigneePhone.value.trim())
  if (!email && !phone) return []
  return contacts.value.filter((c: any) => {
    const cEmail = String(c.email || '').trim().toLowerCase()
    const cPhone = normalizePhone(String(c.phone || '').trim())
    const emailMatch = Boolean(email && cEmail && cEmail === email)
    const phoneMatch = Boolean(phone && cPhone && cPhone === phone)
    return emailMatch || phoneMatch
  })
}

function cancelDuplicatePrompt() {
  showDuplicateConsigneePrompt.value = false
  duplicateConsigneeMatches.value = []
}

async function useExistingDuplicate(existingContactId: number, existingOrganizationId?: number | null) {
  try {
    submitting.value = true
    showDuplicateConsigneePrompt.value = false
    showSaveConsigneePrompt.value = false
    contactId.value = existingContactId
    await performReservation(existingContactId, (existingOrganizationId ?? undefined) as number | undefined)
    emit('saved')
    emit('close')
  } catch (e: any) {
    error.value = e?.message || 'Failed to reserve with existing consignee.'
  } finally {
    submitting.value = false
  }
}

async function saveConsigneeAndReserveInternal(skipDuplicateCheck = false) {
  try {
    submitting.value = true
    error.value = ''
    if (!newConsigneeName.value.trim()) {
      error.value = 'Consignee name is required to save.'
      showSaveConsigneePrompt.value = false
      return
    }

    if (!skipDuplicateCheck) {
      const matches = getDuplicateConsigneeMatches()
      if (matches.length > 0) {
        duplicateConsigneeMatches.value = matches
        showDuplicateConsigneePrompt.value = true
        showSaveConsigneePrompt.value = false
        return
      }
    }

    let resolvedOrganizationId = organizationId.value ?? null
    if (!resolvedOrganizationId && newConsigneeOrganization.value.trim()) {
      const { data: orgRow, error: orgError } = await supabase
        .schema('app' as any)
        .from('organizations')
        .insert({ name: newConsigneeOrganization.value.trim(), type: 'customer' })
        .select('id')
        .single()
      if (orgError) throw orgError
      resolvedOrganizationId = orgRow.id
    }

    const { data: newContact, error: contactError } = await supabase
      .schema('app' as any)
      .from('contacts')
      .insert({
        organization_id: resolvedOrganizationId,
        name: newConsigneeName.value.trim(),
        title: newConsigneeTitle.value.trim() || null,
        email: newConsigneeEmail.value.trim() || null,
        phone: newConsigneePhone.value.trim() || null,
        address1: newConsigneeAddress1.value.trim() || null,
        address2: newConsigneeAddress2.value.trim() || null,
        city: newConsigneeCity.value.trim() || null,
        state: newConsigneeState.value.trim().toUpperCase() || null,
        zip: newConsigneeZip.value.trim() || null,
        shipping_same_as_consignee: newConsigneeShippingSameAsConsignee.value,
        shipping_address1: newConsigneeShippingSameAsConsignee.value
          ? null
          : (newConsigneeShippingAddress1.value.trim() || null),
        shipping_address2: newConsigneeShippingSameAsConsignee.value
          ? null
          : (newConsigneeShippingAddress2.value.trim() || null),
        shipping_city: newConsigneeShippingSameAsConsignee.value
          ? null
          : (newConsigneeShippingCity.value.trim() || null),
        shipping_state: newConsigneeShippingSameAsConsignee.value
          ? null
          : (newConsigneeShippingState.value.trim().toUpperCase() || null),
        shipping_zip: newConsigneeShippingSameAsConsignee.value
          ? null
          : (newConsigneeShippingZip.value.trim() || null),
      })
      .select('id')
      .single()
    if (contactError) throw contactError

    await loadContacts()
    contactId.value = newContact.id
    showSaveConsigneePrompt.value = false
    await performReservation(newContact.id, resolvedOrganizationId ?? undefined)
    emit('saved')
    emit('close')
  } catch (e: any) {
    error.value = e?.message || 'Failed to save consignee.'
    showSaveConsigneePrompt.value = false
    showDuplicateConsigneePrompt.value = false
  } finally {
    submitting.value = false
  }
}

async function saveConsigneeAndReserve() {
  await saveConsigneeAndReserveInternal(false)
}

async function createNewDuplicateAnyway() {
  await saveConsigneeAndReserveInternal(true)
}

async function loadOrgs() {
  const { data } = await supabase.schema('app' as any).from('organizations').select('*').order('name')
  orgs.value = data || []
}
async function loadContacts() {
  const { data } = await supabase.schema('app' as any).from('contacts').select('*').order('name')
  contacts.value = data || []
}
async function createOrg() {
  const name = prompt('Organization name')
  if (!name) return
  const type = prompt('Type (prospect/customer/partner/supplier)', 'customer') || 'customer'
  await supabase.schema('app' as any).from('organizations').insert({ name, type })
  await loadOrgs()
}
async function createContact() {
  const name = prompt('Contact name')
  if (!name) return
  const email = prompt('Email') || ''
  const phone = prompt('Phone') || ''
  await supabase
    .schema('app' as any).from('contacts')
    .insert({ name, email, phone, organization_id: organizationId.value || null })
  await loadContacts()
}

function goToConsignees() {
  emit('close')
  router.push('/consignees')
}
onMounted(() => {
  loadOrgs()
  loadContacts()
})

watch(
  [
    newConsigneeShippingSameAsConsignee,
    newConsigneeAddress1,
    newConsigneeAddress2,
    newConsigneeCity,
    newConsigneeState,
    newConsigneeZip,
  ],
  () => {
    if (!newConsigneeShippingSameAsConsignee.value) return
    newConsigneeShippingAddress1.value = newConsigneeAddress1.value
    newConsigneeShippingAddress2.value = newConsigneeAddress2.value
    newConsigneeShippingCity.value = newConsigneeCity.value
    newConsigneeShippingState.value = newConsigneeState.value
    newConsigneeShippingZip.value = newConsigneeZip.value
  },
  { immediate: true },
)
</script>
